import { useCallback } from 'react'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options'
import type { ExpensesTableParams } from '../index'
import type { ExpenseType, ExpenseCategory } from '@/api/expenses'

const TYPE_OPTIONS: { labelKey: string; value: ExpenseType }[] = [
  { labelKey: 'exp_type_cabinet', value: 'cabinet' },
  { labelKey: 'exp_type_dossier', value: 'dossier' },
]

const CATEGORY_OPTIONS: { labelKey: string; value: ExpenseCategory }[] = [
  { labelKey: 'exp_cat_frais_justice',       value: 'frais_justice' },
  { labelKey: 'exp_cat_deplacement',         value: 'deplacement' },
  { labelKey: 'exp_cat_expertise',           value: 'expertise' },
  { labelKey: 'exp_cat_honoraires_confrere', value: 'honoraires_confrere' },
  { labelKey: 'exp_cat_fournitures',         value: 'fournitures' },
  { labelKey: 'exp_cat_communication',       value: 'communication' },
  { labelKey: 'exp_cat_other',               value: 'other' },
]

interface ExpensesToolbarProps<TData> {
  table: Table<TData>
  params: ExpensesTableParams
  setParams: React.Dispatch<React.SetStateAction<ExpensesTableParams>>
}

export function ExpensesToolbar<TData>({
  table,
  params,
  setParams,
}: ExpensesToolbarProps<TData>) {
  const { t } = useTranslation()

  const hasFilters = !!params.type || !!params.category

  const typeOptions = TYPE_OPTIONS.map((o) => ({
    label: t(o.labelKey),
    value: o.value,
  }))

  const categoryOptions = CATEGORY_OPTIONS.map((o) => ({
    label: t(o.labelKey),
    value: o.value,
  }))

  const handleTypeChange = useCallback(
    (values: string[]) => {
      setParams((p) => ({
        ...p,
        type: values.length > 0 ? (values[0] as ExpenseType) : undefined,
        page: 0,
      }))
    },
    [setParams],
  )

  const handleCategoryChange = useCallback(
    (values: string[]) => {
      setParams((p) => ({
        ...p,
        category:
          values.length > 0 ? (values[0] as ExpenseCategory) : undefined,
        page: 0,
      }))
    },
    [setParams],
  )

  const resetAll = () => {
    setParams((p) => ({
      ...p,
      type: undefined,
      category: undefined,
      page: 0,
    }))
  }

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <DataTableFacetedFilter
          title={t('exp_filter_type')}
          options={typeOptions}
          defaultSelected={params.type ? [params.type] : []}
          onFacetChange={handleTypeChange}
        />
        <DataTableFacetedFilter
          title={t('exp_filter_category')}
          options={categoryOptions}
          defaultSelected={params.category ? [params.category] : []}
          onFacetChange={handleCategoryChange}
        />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetAll}
            className="h-9 px-2 text-muted-foreground hover:text-foreground"
          >
            {t('reset_filters')}
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  )
}
